import React, { useEffect, useMemo, useState } from 'react';
import {
  X,
  Home,
  Briefcase,
  Ticket,
  User,
  Camera,
  Check,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  ShieldCheck,
  MapPin,
  Phone,
  Wifi,
  Battery,
  Signal,
  Zap,
  Banknote,
  Navigation,
  Globe,
  Clock,
  Bell,
  LogOut,
  Car,
  Locate,
  MessageCircle,
  Sparkles,
  Mail,
  FileText,
  Wallet,
  ArrowLeftRight,
} from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';
import ServiceAreaMapEditor, {
  geocodePlace,
  parseHex,
  regularHexagon,
  serializeHex,
  PUNE,
} from './ServiceAreaMapEditor';

const headers = {
  apikey: SUPABASE_ANON_KEY,
  Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  'Content-Type': 'application/json',
};

const SPECS = ['Jump Start', 'Towing', 'Flat Tyre', 'Minor Repair', 'Key Lockout', 'Fuel Delivery'];
const BULK = [
  { qty: 1, discountPct: 0 },
  { qty: 5, discountPct: 5 },
  { qty: 10, discountPct: 10 },
];
const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'gu', label: 'ગુજરાતી' },
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DEFAULT_SLOTS = [0, 1, 2, 3, 4, 5, 6].map((d) => ({
  day_of_week: d,
  start_time: '09:00',
  end_time: '21:00',
  is_enabled: d >= 1 && d <= 6,
}));
const HOUR_PRESETS = [
  { id: '24h', label: '24 hours', start: '00:00', end: '23:59' },
  { id: '8to8', label: '8–8', start: '08:00', end: '20:00' },
  { id: '9to9', label: '9–9', start: '09:00', end: '21:00' },
  { id: '10to10', label: '10–10', start: '10:00', end: '22:00' },
];

const normalizeSlots = (list) => {
  const byDay = new Map();
  (list || []).forEach((s) => {
    const dow = Number(s.day_of_week);
    if (dow >= 0 && dow <= 6) {
      byDay.set(dow, {
        day_of_week: dow,
        start_time: String(s.start_time || '09:00').slice(0, 5),
        end_time: String(s.end_time || '21:00').slice(0, 5),
        is_enabled: s.is_enabled !== false,
      });
    }
  });
  return DEFAULT_SLOTS.map((d) => byDay.get(d.day_of_week) || { ...d });
};

const summarizeHours = (slots) => {
  const enabled = (slots || []).filter((s) => s.is_enabled);
  if (!enabled.length) return 'Off';
  const all24 =
    enabled.length === 7 &&
    enabled.every(
      (s) => s.start_time === '00:00' && (s.end_time === '23:59' || s.end_time === '24:00'),
    );
  if (all24) return '24 hours';
  const same = enabled.every(
    (s) => s.start_time === enabled[0].start_time && s.end_time === enabled[0].end_time,
  );
  if (same) return `${enabled[0].start_time}–${enabled[0].end_time}`;
  return `${enabled.length} days`;
};

const rpc = async (name, body) => {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${name}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error || data?.message || `${name} failed`);
  }
  return data;
};

const formatInr = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const jobStatus = (job) => String(job?.request_status || '').toLowerCase();
const isActiveJob = (job) => {
  const s = `${job?.request_status || ''} ${job?.complaint_status || ''}`.toLowerCase();
  return !s.includes('complete') && !s.includes('cancel');
};
const isMemberJob = (job) =>
  job?.pricing_mode === 'membership_covered' || job?.is_premium === true;
const needsQuote = (job) =>
  !isMemberJob(job) &&
  ['awaiting_mechanic_quote', 'negotiating', ''].includes(String(job?.rate_status || ''));

const serviceTint = (type) => {
  const s = String(type || '').toLowerCase();
  if (s.includes('tow')) return { bg: '#FEF2F2', fg: '#E02020', label: 'Towing' };
  if (s.includes('fuel')) return { bg: '#ECFDF5', fg: '#059669', label: 'Fuel' };
  if (s.includes('jump') || s.includes('battery')) return { bg: '#FFFBEB', fg: '#D97706', label: 'Jump start' };
  if (s.includes('tyre') || s.includes('tire')) return { bg: '#EFF6FF', fg: '#2563EB', label: 'Tyre' };
  if (s.includes('key') || s.includes('lock')) return { bg: '#F0FDFA', fg: '#0F766E', label: 'Key lockout' };
  return { bg: '#EFF6FF', fg: '#1B4F9C', label: 'Roadside' };
};

const matchPack = (products, tab) => {
  const scored = (products || []).map((p) => {
    const hay = `${p.name || ''} ${(p.allowed_service_types || []).join(' ')}`.toLowerCase();
    if (tab === 'fuel') return { p, score: /fuel/.test(hay) ? 2 : 0 };
    if (tab === 'towing') return { p, score: /tow/.test(hay) ? 2 : 0 };
    const isRoad = /roadside|jump|tyre|tire|lockout|lock|minor|repair/.test(hay) && !/^fuel|^tow/.test((p.name || '').toLowerCase());
    return { p, score: isRoad ? 2 : /assist|rsa/.test(hay) ? 1 : 0 };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0]?.score ? scored[0].p : products?.[0];
};

const bulkPreview = (product, qty) => {
  const unit = Number(product?.price_inr || 0);
  const list = Math.round(unit * qty * 100) / 100;
  const pct = qty === 10 ? 10 : qty === 5 ? 5 : 0;
  const disc = Math.round(((list * pct) / 100) * 100) / 100;
  const final = Math.max(0, Math.round((list - disc) * 100) / 100);
    const credits = Math.round(Number(product?.credits_inr ?? product?.price_inr ?? 0) * qty);
  return { list, disc, pct, final, credits, tickets: (Number(product?.tickets_count) || 1) * qty };
};

const MechanicAppPreviewModal = ({ mechanicId, initialGroup, onClose, onChanged }) => {
  const [tab, setTab] = useState('home');
  const [stack, setStack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [banners, setBanners] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [tickets, setTickets] = useState(null);
  const [products, setProducts] = useState([]);
  const [jobFilter, setJobFilter] = useState('all');
  const [expandedJob, setExpandedJob] = useState(null);
  const [packTab, setPackTab] = useState('roadside');
  const [qty, setQty] = useState(10);
  const [locale, setLocale] = useState('en');
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editExtra, setEditExtra] = useState('');
  const [editSpecs, setEditSpecs] = useState([]);
  const [editLat, setEditLat] = useState(PUNE.lat);
  const [editLng, setEditLng] = useState(PUNE.lng);
  const [editHex, setEditHex] = useState(() => regularHexagon(PUNE, 15));
  const [editShape, setEditShape] = useState('circle');
  const [aiWriting, setAiWriting] = useState(false);
  const [slots, setSlots] = useState(() => normalizeSlots(DEFAULT_SLOTS));
  const [crew, setCrew] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [openPurchase, setOpenPurchase] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [walletBusy, setWalletBusy] = useState('');
  const [notifPrefs, setNotifPrefs] = useState({ push: true, whatsapp: true, call: true });
  const [clock, setClock] = useState(() =>
    new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
  );

  const load = async () => {
    if (!mechanicId) return;
    setLoading(true);
    try {
      const [mechRes, status, jobRes, bannerRes, bal, catalog, nearby, slotRows, crewRows, purchaseRows, walletRes] =
        await Promise.all([
        fetch(`${SUPABASE_URL}/rest/v1/mechanics?id=eq.${mechanicId}&select=*&limit=1`, { headers }).then((r) => r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_get_profile_status`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ p_mechanic_id: mechanicId }),
        }).then((r) => r.json()),
        fetch(
          `${SUPABASE_URL}/rest/v1/service_requests?select=*&assigned_mechanic_id=eq.${encodeURIComponent(mechanicId)}&order=updated_at.desc.nullslast&limit=40`,
          { headers },
        ).then((r) => r.json()),
        fetch(
          `${SUPABASE_URL}/rest/v1/mechanic_app_banners?select=id,title,subtitle,image_url,position,is_active&is_active=eq.true&order=position.desc,created_at.desc`,
          { headers },
        ).then((r) => r.json()),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_get_ticket_balance`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ p_mechanic_id: mechanicId, p_service_type: null }),
        })
          .then((r) => r.json())
          .catch(() => null),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_list_ticket_products`, {
          method: 'POST',
          headers,
          body: JSON.stringify({}),
        })
          .then((r) => r.json())
          .catch(() => []),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_list_nearby_open_rsa_jobs`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            p_mechanic_id: mechanicId,
            p_lat: null,
            p_lng: null,
            p_radius_km: 40,
          }),
        })
          .then((r) => r.json())
          .catch(() => null),
        fetch(
          `${SUPABASE_URL}/rest/v1/mechanic_availability_slots?mechanic_id=eq.${mechanicId}&select=day_of_week,start_time,end_time,is_enabled&order=day_of_week.asc`,
          { headers },
        )
          .then((r) => r.json())
          .catch(() => []),
        fetch(
          `${SUPABASE_URL}/rest/v1/partner_crew_members?partner_id=eq.${mechanicId}&select=id,display_name,phone,status&order=created_at.asc&limit=50`,
          { headers },
        )
          .then((r) => r.json())
          .catch(() => []),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_list_ticket_purchases`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ p_mechanic_id: mechanicId, p_limit: 50 }),
        })
          .then((r) => r.json())
          .catch(() => []),
        fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_wallet_summary`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ p_mechanic_id: mechanicId, p_limit: 20 }),
        })
          .then((r) => r.json())
          .catch(() => null),
      ]);

      const mech = Array.isArray(mechRes) ? mechRes[0] : null;
      const st = status?.success === false ? null : status;
      let owner = null;
      if (mech?.owner_user_id) {
        const users = await fetch(
          `${SUPABASE_URL}/rest/v1/users?id=eq.${mech.owner_user_id}&select=id,name,email,phone&limit=1`,
          { headers },
        ).then((r) => r.json());
        owner = Array.isArray(users) ? users[0] : null;
      }

      setProfile({
        mechanic_name: st?.mechanic_name || mech?.mechanic_name || initialGroup?.shop_name || '',
        shop_name: st?.shop_name || mech?.shop_name || initialGroup?.shop_name || '',
        phone: mech?.number || owner?.phone || initialGroup?.phone || '',
        email: owner?.email || '',
        city: st?.city || mech?.city || '',
        area: st?.area || mech?.area || '',
        bio: st?.bio || mech?.bio || '',
        logo_url: st?.logo_url || mech?.logo_url || '',
        service_tag: st?.service_tag || mech?.service_tag || '',
        experience_years: st?.experience_years ?? mech?.experience_years ?? '',
        partner_type: mech?.partner_type || 'mechanic',
        active: !!(st?.active ?? mech?.active),
        is_public: !!(st?.is_public ?? mech?.is_public),
        kyc_status: st?.kyc_status || mech?.kyc_status || 'pending',
        public_slug: st?.public_slug || mech?.public_slug || '',
        public_url_path: st?.public_url_path || '',
        service_radius_km: mech?.service_radius_km || null,
        service_center_lat: mech?.service_center_lat ?? null,
        service_center_lng: mech?.service_center_lng ?? null,
        service_area_hex: mech?.service_area_hex ?? null,
        owner_user_id: mech?.owner_user_id || null,
        documents: Array.isArray(st?.documents) ? st.documents : initialGroup?.docs || [],
      });
      setCrew(Array.isArray(crewRows) ? crewRows : []);
      const paidList = Array.isArray(purchaseRows)
        ? purchaseRows
        : Array.isArray(purchaseRows?.data)
          ? purchaseRows.data
          : [];
      setPurchases(paidList);
      setJobs(Array.isArray(jobRes) ? jobRes : []);
      setBanners(Array.isArray(bannerRes) ? bannerRes : []);
      setTickets(bal?.success === false ? null : bal);
      setProducts(Array.isArray(catalog) ? catalog : []);
      setWallet(walletRes?.success === false ? { success: false, error: walletRes.error } : walletRes);
      const nearbyJobs = Array.isArray(nearby?.jobs) ? nearby.jobs : [];
      setOpenJobs(nearbyJobs);
      setSlots(normalizeSlots(Array.isArray(slotRows) ? slotRows : []));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [mechanicId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (editField) setEditField(null);
        else if (stack) setStack(null);
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const tick = setInterval(() => {
      setClock(
        new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false }),
      );
    }, 30000);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      clearInterval(tick);
    };
  }, [onClose, editField, stack]);

  const displayName = profile?.shop_name || profile?.mechanic_name || 'Partner';
  const typeLabel =
    profile?.partner_type === 'towing'
      ? 'Towing'
      : profile?.partner_type === 'both'
        ? 'Mech + Towing'
        : 'Mechanic';
  const cityLabel = [profile?.city, profile?.area].filter(Boolean).join(', ') || 'Location off';
  const specs = useMemo(
    () =>
      String(profile?.service_tag || '')
        .split(/[,·|/]/)
        .map((s) => s.trim())
        .filter(Boolean),
    [profile?.service_tag],
  );

  const liveSteps = useMemo(() => {
    const docs = profile?.documents || [];
    const uploaded = (type, aliases = []) =>
      docs.some((d) => (d.doc_type === type || aliases.includes(d.doc_type)) && (d.status === 'pending' || d.status === 'approved'));
    const kycVerified = profile?.kyc_status === 'verified';
    return [
      { id: 'photo', title: 'Upload profile photo', done: !!profile?.logo_url },
      { id: 'city', title: 'City + area from your location', done: !!(profile?.city?.trim() && profile?.area?.trim()) },
      { id: 'specialization', title: 'Select your specialization (RSA services)', done: specs.length > 0 },
      { id: 'service', title: 'Set service area map + radius', done: !!profile?.service_radius_km },
      {
        id: 'docs',
        title: 'Upload mandatory KYC (selfie + Aadhaar front/back)',
        done: kycVerified || (uploaded('selfie') && uploaded('aadhaar_front', ['aadhaar']) && uploaded('aadhaar_back')),
      },
      { id: 'admin', title: 'Wait for approval', done: kycVerified },
    ];
  }, [profile, specs]);

  const isVerifiedLive = profile?.kyc_status === 'verified' && liveSteps.every((s) => s.done);
  const activeJobs = jobs.filter(isActiveJob);
  const quoteCount = activeJobs.filter(needsQuote).length;
  const memberCount = activeJobs.filter(isMemberJob).length;
  const filteredJobs = jobs.filter((j) => {
    if (jobFilter === 'all') return true;
    const s = jobStatus(j);
    if (jobFilter === 'assigned') return s === 'assigned' || s === 'pending';
    if (jobFilter === 'in_progress') return s.includes('progress') || s.includes('start');
    if (jobFilter === 'completed') return s.includes('complete');
    return true;
  });
  const jobCounts = {
    total: jobs.length,
    active: activeJobs.length,
    done: jobs.filter((j) => jobStatus(j).includes('complete')).length,
  };
  const selectedProduct = matchPack(products, packTab);
  const preview = selectedProduct ? bulkPreview(selectedProduct, qty) : null;

  const patchProfile = async (partial) => {
    if (!mechanicId) return;
    setSaving(true);
    try {
      const next = { ...profile, ...partial };
      await fetch(`${SUPABASE_URL}/rest/v1/mechanics?id=eq.${mechanicId}`, {
        method: 'PATCH',
        headers: { ...headers, Prefer: 'return=minimal' },
        body: JSON.stringify({
          mechanic_name: next.mechanic_name || null,
          shop_name: next.shop_name || null,
          number: next.phone || null,
          city: next.city || null,
          area: next.area || null,
          bio: next.bio || null,
          logo_url: next.logo_url || null,
          service_tag: next.service_tag || null,
          experience_years: next.experience_years === '' ? null : Number(next.experience_years),
          service_radius_km:
            next.service_radius_km === '' || next.service_radius_km == null
              ? null
              : Number(next.service_radius_km),
          service_center_lat:
            next.service_center_lat == null || next.service_center_lat === ''
              ? null
              : Number(next.service_center_lat),
          service_center_lng:
            next.service_center_lng == null || next.service_center_lng === ''
              ? null
              : Number(next.service_center_lng),
          ...(Array.isArray(next.service_area_hex) ? { service_area_hex: next.service_area_hex } : {}),
          partner_type: next.partner_type || 'mechanic',
          is_public: !!next.is_public,
          active: !!next.active,
        }),
      });
      if (next.owner_user_id && (partial.email != null || partial.phone != null)) {
        try {
          await fetch(`${SUPABASE_URL}/rest/v1/users?id=eq.${next.owner_user_id}`, {
            method: 'PATCH',
            headers: { ...headers, Prefer: 'return=minimal' },
            body: JSON.stringify({
              ...(partial.email != null ? { email: next.email } : {}),
              ...(partial.phone != null ? { phone: next.phone } : {}),
            }),
          });
        } catch {
          /* optional */
        }
      }
      const lat = Number(next.service_center_lat);
      const lng = Number(next.service_center_lng);
      const radius = Number(next.service_radius_km);
      if (Number.isFinite(lat) && Number.isFinite(lng) && Number.isFinite(radius) && radius > 0) {
        try {
          await rpc('partner_upsert_service_area', {
            p_mechanic_id: mechanicId,
            p_lat: lat,
            p_lng: lng,
            p_radius_km: Math.round(radius),
          });
        } catch {
          /* PATCH already wrote radius */
        }
      }
      try {
        await rpc('partner_update_public_profile', {
          p_mechanic_id: mechanicId,
          p_shop_name: next.shop_name || null,
          p_bio: next.bio || null,
          p_city: next.city || null,
          p_area: next.area || null,
          p_logo_url: next.logo_url || null,
          p_service_tag: next.service_tag || null,
          p_experience_years: next.experience_years === '' ? null : Number(next.experience_years),
        });
      } catch {
        /* PATCH is enough */
      }
      setProfile(next);
      onChanged?.();
    } finally {
      setSaving(false);
      setEditField(null);
    }
  };

  const toggleOnline = () => patchProfile({ active: !profile.active });

  const uploadLogo = async (file) => {
    if (!file || !mechanicId) return;
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const path = `logos/${mechanicId}/${Date.now()}.${ext}`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/partner-profiles/${path}`, {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': file.type || 'image/jpeg',
          'x-upsert': 'true',
        },
        body: file,
      });
      if (!res.ok) throw new Error('Photo upload failed');
      const url = `${SUPABASE_URL}/storage/v1/object/public/partner-profiles/${path}`;
      await patchProfile({ logo_url: url });
    } catch (e) {
      console.error(e);
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (field) => {
    if (field === 'specialization') {
      setEditSpecs(specs);
      setEditField(field);
      return;
    }
    if (field === 'name') setEditValue(profile.shop_name || profile.mechanic_name || '');
    else if (field === 'phone') setEditValue(profile.phone || '');
    else if (field === 'email') setEditValue(profile.email || '');
    else if (field === 'city' || field === 'place') {
      setEditValue(profile.city || '');
      setEditExtra(profile.area || '');
      setEditField('place');
      return;
    } else if (field === 'area') setEditValue(profile.area || '');
    else if (field === 'bio') setEditValue(profile.bio || '');
    else if (field === 'type') setEditValue(profile.partner_type || 'mechanic');
    else if (field === 'experience') setEditValue(profile.experience_years === '' ? '' : String(profile.experience_years));
    else if (field === 'radius') {
      const km = Math.max(1, Math.min(50, Math.round(Number(profile.service_radius_km) || 15)));
      setEditValue(String(km));
      const latN = Number(profile.service_center_lat);
      const lngN = Number(profile.service_center_lng);
      const hasPin = Number.isFinite(latN) && Number.isFinite(lngN);
      const start = hasPin ? { lat: latN, lng: lngN } : PUNE;
      setEditLat(start.lat);
      setEditLng(start.lng);
      setEditHex(parseHex(profile.service_area_hex, start, km));
      setEditShape(Array.isArray(profile.service_area_hex) && profile.service_area_hex.length === 6 ? 'hex' : 'circle');
      setEditField(field);
      if (!hasPin && (profile.city || profile.area)) {
        geocodePlace(profile.city, profile.area).then((pos) => {
          if (!pos) return;
          setEditLat(pos.lat);
          setEditLng(pos.lng);
          setEditHex(regularHexagon(pos, km));
        });
      }
      return;
    }
    setEditField(field);
  };

  const generateBio = async () => {
    setAiWriting(true);
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/partner-bio-ai-generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          shop_name: profile?.shop_name || profile?.mechanic_name || '',
          city: profile?.city || '',
          area: profile?.area || '',
          partner_type: profile?.partner_type || 'mechanic',
          language: 'en',
          existing_bio: editValue || profile?.bio || '',
          specialization: profile?.service_tag || '',
        }),
      });
      const data = await res.json().catch(() => ({}));
      const bio = String(data?.bio || '').trim();
      if (bio) setEditValue(bio);
    } catch (e) {
      console.error(e);
    } finally {
      setAiWriting(false);
    }
  };

  const saveEdit = () => {
    if (editField === 'specialization') {
      patchProfile({ service_tag: SPECS.filter((s) => editSpecs.includes(s)).join(', ') });
      return;
    }
    if (editField === 'name') {
      patchProfile({ shop_name: editValue.trim(), mechanic_name: editValue.trim() });
      return;
    }
    if (editField === 'phone') patchProfile({ phone: editValue.trim() });
    if (editField === 'email') patchProfile({ email: editValue.trim() });
    if (editField === 'place') patchProfile({ city: editValue.trim(), area: editExtra.trim() });
    if (editField === 'city') patchProfile({ city: editValue.trim() });
    if (editField === 'area') patchProfile({ area: editValue.trim() });
    if (editField === 'bio') patchProfile({ bio: editValue.trim() });
    if (editField === 'type') patchProfile({ partner_type: editValue });
    if (editField === 'experience') patchProfile({ experience_years: editValue.trim() });
    if (editField === 'radius') {
      const km = Math.max(1, Math.min(50, Math.round(Number(editValue) || 15)));
      const hex = editShape === 'hex' ? serializeHex(editHex) : null;
      patchProfile({
        service_radius_km: km,
        service_center_lat: editLat,
        service_center_lng: editLng,
        service_area_hex: hex,
      });
      fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_upsert_service_area`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          p_mechanic_id: mechanicId,
          p_lat: editLat,
          p_lng: editLng,
          p_radius_km: km,
          p_hex: hex,
        }),
      }).catch(() => {});
    }
  };

  const goTab = (id) => {
    setStack(null);
    setTab(id);
  };

  const convertPack = async (product) => {
    if (!product?.id || !mechanicId) return;
    setWalletBusy(product.id);
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_redeem_credits_for_tickets`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          p_mechanic_id: mechanicId,
          p_product_id: product.id,
          p_quantity: 1,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        alert(data?.error || data?.message || 'Wallet se convert nahi hua');
        return;
      }
      await load();
    } catch (e) {
      alert(e?.message || 'Convert failed');
    } finally {
      setWalletBusy('');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div className="relative flex max-h-[100%] w-full max-w-[430px] flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex w-full items-center justify-between px-1 text-white">
          <p className="text-xs font-semibold text-white/80">Partner app · {displayName}</p>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold hover:bg-white/20"
          >
            <X size={14} />
            Close
          </button>
        </div>

        <div
          className="relative w-full overflow-hidden rounded-[42px] bg-[#111214] p-[10px] shadow-[0_30px_80px_rgba(0,0,0,0.55)] ring-1 ring-white/10"
          style={{ maxHeight: 'min(860px, calc(100vh - 56px))' }}
        >
          <div className="flex h-[min(820px,calc(100vh-76px))] flex-col overflow-hidden rounded-[32px] bg-[#F4F7FB]">
            <div className="relative z-20 flex h-11 shrink-0 items-end justify-between bg-white px-6 pb-1">
              <span className="text-[12px] font-bold text-slate-900">{clock}</span>
              <div className="absolute left-1/2 top-1.5 h-6 w-[92px] -translate-x-1/2 rounded-full bg-black" />
              <div className="flex items-center gap-1 text-slate-900">
                <Signal size={12} />
                <Wifi size={12} />
                <Battery size={14} />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-[#F4F7FB]">
              {loading || !profile ? (
                <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-slate-500">
                  <Loader2 className="animate-spin text-[#1B4F9C]" size={28} />
                  Loading…
                </div>
              ) : stack === 'public' ? (
                <PublicScreen
                  profile={profile}
                  isVerifiedLive={isVerifiedLive}
                  onBack={() => setStack(null)}
                  onEdit={openEdit}
                  onUploadLogo={uploadLogo}
                  uploading={uploading}
                />
              ) : stack === 'team' ? (
                <TeamScreen
                  crew={crew}
                  saving={saving}
                  onBack={() => setStack(null)}
                  onAdd={async (member) => {
                    setSaving(true);
                    try {
                      const res = await fetch(`${SUPABASE_URL}/rest/v1/partner_crew_members`, {
                        method: 'POST',
                        headers: { ...headers, Prefer: 'return=representation' },
                        body: JSON.stringify({
                          partner_id: mechanicId,
                          display_name: member.name,
                          phone: member.phone,
                          status: 'active',
                        }),
                      });
                      const rows = await res.json().catch(() => []);
                      if (Array.isArray(rows) && rows[0]) setCrew((prev) => [...prev, rows[0]]);
                    } finally {
                      setSaving(false);
                    }
                  }}
                />
              ) : stack === 'wallet' ? (
                <WalletScreen
                  wallet={wallet}
                  products={products}
                  busyId={walletBusy}
                  onBack={() => setStack(null)}
                  onConvert={convertPack}
                />
              ) : stack === 'history' ? (
                <HistoryScreen
                  purchases={purchases}
                  openPurchase={openPurchase}
                  setOpenPurchase={setOpenPurchase}
                  onBack={() => {
                    setOpenPurchase(null);
                    setStack(null);
                  }}
                  onBuy={() => {
                    setOpenPurchase(null);
                    goTab('tickets');
                  }}
                />
              ) : stack === 'notif' ? (
                <NotifScreen
                  prefs={notifPrefs}
                  setPrefs={setNotifPrefs}
                  onBack={() => setStack(null)}
                />
              ) : stack === 'hours' ? (
                <HoursScreen
                  slots={slots}
                  saving={saving}
                  onBack={() => setStack(null)}
                  onSave={async (next) => {
                    setSaving(true);
                    try {
                      const payload = next.map((s) => ({
                        day_of_week: s.day_of_week,
                        start_time: s.start_time.length === 5 ? `${s.start_time}:00` : s.start_time,
                        end_time: s.end_time.length === 5 ? `${s.end_time}:00` : s.end_time,
                        is_enabled: !!s.is_enabled,
                      }));
                      await fetch(`${SUPABASE_URL}/rest/v1/rpc/partner_upsert_availability_slots`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({ p_mechanic_id: mechanicId, p_slots: payload }),
                      });
                      setSlots(normalizeSlots(next));
                      onChanged?.();
                      setStack(null);
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setSaving(false);
                    }
                  }}
                />
              ) : tab === 'home' ? (
                <HomeScreen
                  displayName={displayName}
                  cityLabel={cityLabel}
                  profile={profile}
                  banners={banners}
                  bannerIndex={bannerIndex}
                  setBannerIndex={setBannerIndex}
                  activeJobs={activeJobs}
                  quoteCount={quoteCount}
                  memberCount={memberCount}
                  openJobs={openJobs}
                  tickets={tickets}
                  onToggleOnline={toggleOnline}
                  onTab={goTab}
                  onHours={() => setStack('hours')}
                  onExpand={setExpandedJob}
                  expandedJob={expandedJob}
                />
              ) : tab === 'jobs' ? (
                <JobsScreen
                  typeLabel={typeLabel}
                  jobs={filteredJobs}
                  counts={jobCounts}
                  jobFilter={jobFilter}
                  setJobFilter={setJobFilter}
                  expandedJob={expandedJob}
                  setExpandedJob={setExpandedJob}
                />
              ) : tab === 'tickets' ? (
                <TicketsScreen
                  products={products}
                  packTab={packTab}
                  setPackTab={setPackTab}
                  qty={qty}
                  setQty={setQty}
                  selectedProduct={selectedProduct}
                  preview={preview}
                  wallet={wallet}
                  busyId={walletBusy}
                  onWallet={() => setStack('wallet')}
                  onConvert={convertPack}
                />
              ) : (
                <ProfileScreen
                  profile={profile}
                  displayName={displayName}
                  typeLabel={typeLabel}
                  cityLabel={cityLabel}
                  specs={specs}
                  tickets={tickets}
                  liveSteps={liveSteps}
                  isVerifiedLive={isVerifiedLive}
                  locale={locale}
                  setLocale={setLocale}
                  uploading={uploading}
                  onUploadLogo={uploadLogo}
                  onEdit={openEdit}
                  onPublic={() => setStack('public')}
                  onHours={() => setStack('hours')}
                  onHistory={() => setStack('history')}
                  onWallet={() => setStack('wallet')}
                  wallet={wallet}
                  purchaseCount={purchases.length}
                  onTeam={() => setStack('team')}
                  onNotif={() => setStack('notif')}
                  onTogglePublic={() => patchProfile({ is_public: !profile.is_public })}
                  hoursSummary={summarizeHours(slots)}
                  crewCount={crew.length}
                  notifSummary={`${notifPrefs.push ? 'Push' : ''}${notifPrefs.whatsapp ? ' · WhatsApp' : ''}${notifPrefs.call ? ' · Call' : ''}`.replace(/^ · /, '') || 'All off'}
                  onTab={goTab}
                />
              )}
            </div>

            {editField ? (
              <EditSheet
                field={editField}
                value={editValue}
                setValue={setEditValue}
                extra={editExtra}
                setExtra={setEditExtra}
                specs={editSpecs}
                setSpecs={setEditSpecs}
                mapLat={editLat}
                mapLng={editLng}
                mapHex={editHex}
                mapShape={editShape}
                onMapChange={({ lat, lng, hex, radiusKm, shape }) => {
                  if (lat != null) setEditLat(lat);
                  if (lng != null) setEditLng(lng);
                  if (hex) setEditHex(hex);
                  if (radiusKm != null) setEditValue(String(radiusKm));
                  if (shape) setEditShape(shape);
                }}
                saving={saving}
                aiWriting={aiWriting}
                onAiWrite={generateBio}
                onClose={() => setEditField(null)}
                onSave={saveEdit}
              />
            ) : null}

            <div className="shrink-0 border-t border-slate-200 bg-white pb-2 pt-1.5">
              <div className="grid grid-cols-4">
                {[
                  { id: 'home', label: 'Home', Icon: Home },
                  { id: 'jobs', label: 'Jobs', Icon: Briefcase },
                  { id: 'tickets', label: 'Tickets', Icon: Ticket },
                  { id: 'profile', label: 'Profile', Icon: User },
                ].map(({ id, label, Icon }) => {
                  const active = tab === id && !stack;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => goTab(id)}
                      className={`flex flex-col items-center gap-0.5 py-1 text-[10px] font-bold ${
                        active ? 'text-[#1B4F9C]' : 'text-slate-400'
                      }`}
                    >
                      <Icon size={18} strokeWidth={active ? 2.4 : 1.8} />
                      {label}
                    </button>
                  );
                })}
              </div>
              <div className="mx-auto mt-1 h-1 w-28 rounded-full bg-slate-300" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HomeScreen = ({
  displayName,
  cityLabel,
  profile,
  banners,
  bannerIndex,
  setBannerIndex,
  activeJobs,
  quoteCount,
  memberCount,
  openJobs,
  tickets,
  onToggleOnline,
  onTab,
  onHours,
  onExpand,
  expandedJob,
}) => (
  <div className="pb-6">
    <div className="border-b border-slate-200 bg-white px-4 pb-3 pt-1">
      <div className={`rounded-[14px] px-3.5 py-3 ${profile.active ? 'bg-emerald-50' : 'bg-slate-100'}`}>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className={`h-2 w-2 rounded-full ${profile.active ? 'bg-emerald-500' : 'bg-slate-400'}`} />
              <p className="truncate text-[14px] font-extrabold text-[#0F2744]">{displayName}</p>
            </div>
            <p className="mt-1 flex items-center gap-1 text-[12px] font-semibold text-slate-500">
              <MapPin size={12} />
              {cityLabel}
            </p>
          </div>
          <button type="button" onClick={onToggleOnline} className="w-[52px] shrink-0 text-center">
            <span className={`relative mx-auto block h-7 w-[46px] rounded-full p-0.5 ${profile.active ? 'bg-green-300' : 'bg-slate-300'}`}>
              <span
                className={`block h-6 w-6 rounded-full shadow ${profile.active ? 'ml-auto bg-emerald-600' : 'bg-white'}`}
              />
            </span>
            <span className={`mt-0.5 block text-[10px] font-bold ${profile.active ? 'text-emerald-700' : 'text-slate-500'}`}>
              {profile.active ? 'Online' : 'Offline'}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div className="px-4 pt-2.5">
      {banners.length ? (
        <div>
          <div className="overflow-hidden rounded-2xl bg-[#EFF6FF] shadow-sm">
            <img
              src={banners[bannerIndex % banners.length].image_url}
              alt=""
              className="h-[140px] w-full object-contain"
            />
          </div>
          {banners.length > 1 ? (
            <div className="mt-2.5 flex justify-center gap-1.5">
              {banners.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setBannerIndex(i)}
                  className={`h-1.5 rounded-full ${i === bannerIndex ? 'w-[18px] bg-[#1B4F9C]' : 'w-1.5 bg-slate-300'}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <div className="flex h-[140px] flex-col justify-end rounded-2xl bg-gradient-to-br from-[#0F2744] via-[#1B4F9C] to-[#2563EB] p-3.5 text-white">
          <p className="text-[16px] font-extrabold">Partner dashboard</p>
          <p className="text-[12px] font-semibold text-white/90">Admin se banner publish karein</p>
        </div>
      )}

      <div className="mt-3.5 grid grid-cols-3 gap-2.5">
        <Kpi icon={<Zap size={14} className="text-[#1B4F9C]" />} iconBg="#DBEAFE" bg="from-blue-50" n={activeJobs.length} l="Live jobs" />
        <Kpi icon={<Banknote size={14} className="text-amber-600" />} iconBg="#FEF3C7" bg="from-amber-50" n={quoteCount} l="Need rate" />
        <Kpi icon={<ShieldCheck size={14} className="text-emerald-600" />} iconBg="#D1FAE5" bg="from-emerald-50" n={memberCount} l="Member" />
      </div>
    </div>

    {profile.active ? (
      <div className="mt-4">
        <SectionHead
          accent="#D97706"
          title="Nearby — Accept"
          sub={openJobs.length ? `${openJobs.length} nearby · ${tickets?.total_available ?? 0} Available` : 'Listening for nearby RSA jobs…'}
          action="Buy tickets"
          onAction={() => onTab('tickets')}
        />
        {openJobs.length === 0 ? (
          <div className="mx-4 mb-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-[12px] font-semibold leading-relaxed text-slate-500">
            When a customer requests help, Accept will show here and the bell will ring.
          </div>
        ) : (
          openJobs.slice(0, 4).map((oj) => (
            <div key={oj.id} className="mx-4 mb-2.5 rounded-[14px] border border-amber-200 bg-white p-3.5">
              <div className="flex gap-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-extrabold text-slate-900">{oj.service_type || 'RSA'}</p>
                  <p className="mt-1 text-[12px] leading-snug text-slate-500">{oj.pickup_address || 'Location'}</p>
                </div>
                <span className={`h-fit rounded-lg px-2 py-0.5 text-[10px] font-extrabold ${oj.is_membership_covered ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                  {oj.is_membership_covered ? 'MEMBER' : 'QUOTE'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    ) : null}

    <div className="mt-2">
      <SectionHead title="Incoming jobs" sub="Distance · location · problem" action="All jobs" onAction={() => onTab('jobs')} />
      {activeJobs.length === 0 ? (
        <div className="mx-4 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-blue-50 to-white px-4 py-7 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-100 text-[#1B4F9C]">
            <Car size={26} />
          </div>
          <p className="text-[15px] font-extrabold text-slate-900">Waiting for jobs</p>
          <p className="mt-1 text-[12px] font-semibold leading-relaxed text-slate-500">
            Assign hone par yahan pickup/drop, customer aur aapse distance dikhega.
          </p>
        </div>
      ) : (
        <div className="px-4">
          {activeJobs.slice(0, 5).map((job) => (
            <JobCard key={job.id} job={job} expanded={expandedJob === job.id} onToggle={() => onExpand(expandedJob === job.id ? null : job.id)} />
          ))}
        </div>
      )}
    </div>

    <div className="mt-4 grid grid-cols-4 gap-2 px-4">
      {[
        { label: 'Buy tickets', bg: '#FEF2F2', Icon: Ticket, fg: '#E02020', go: 'tickets' },
        { label: 'Hours', bg: '#FEF3C7', Icon: Clock, fg: '#D97706', hours: true },
        { label: 'Language', bg: '#ECFDF5', Icon: Globe, fg: '#059669', go: 'profile' },
        { label: 'Logout', bg: '#F1F5F9', Icon: LogOut, fg: '#0F2744' },
      ].map((q) => (
        <button
          key={q.label}
          type="button"
          onClick={() => {
            if (q.hours) onHours?.();
            else if (q.go) onTab(q.go);
          }}
          className="flex flex-col items-center gap-1.5"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: q.bg, color: q.fg }}>
            <q.Icon size={18} />
          </span>
          <span className="text-[10px] font-bold text-slate-600">{q.label}</span>
        </button>
      ))}
    </div>
  </div>
);

const JobsScreen = ({ typeLabel, jobs, counts, jobFilter, setJobFilter, expandedJob, setExpandedJob }) => (
  <div className="pb-6">
    <div className="bg-gradient-to-br from-[#0F2744] via-[#1B4F9C] to-[#2563EB] px-4 pb-4 pt-2 text-white">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[22px] font-extrabold">Jobs</p>
          <p className="text-[12px] font-semibold text-white/80">{typeLabel} · distance · rate</p>
        </div>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15">
          <Locate size={18} />
        </span>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-2 px-4 pt-3">
      {[
        [counts.total, 'Total'],
        [counts.active, 'Active'],
        [counts.done, 'Done'],
      ].map(([n, l]) => (
        <div key={l} className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
          <p className="text-[22px] font-extrabold text-[#0F2744]">{n}</p>
          <p className="text-[11px] font-bold text-slate-500">{l}</p>
        </div>
      ))}
    </div>
    <div className="mt-3 flex gap-1.5 overflow-x-auto px-4">
      {[
        ['all', 'All'],
        ['assigned', 'Assigned'],
        ['in_progress', 'In progress'],
        ['completed', 'Completed'],
      ].map(([id, label]) => (
        <button
          key={id}
          type="button"
          onClick={() => setJobFilter(id)}
          className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-bold ${
            jobFilter === id ? 'bg-[#1B4F9C] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
    <p className="px-4 pt-2 text-[11px] font-semibold text-slate-500">
      Distance from you · pickup / drop · tap for rate · {jobs.length} shown
    </p>
    <div className="mt-2 px-4">
      {jobs.length === 0 ? (
        <div className="py-16 text-center text-slate-400">
          <Briefcase size={40} className="mx-auto mb-2" />
          <p className="text-sm font-bold">No jobs</p>
        </div>
      ) : (
        jobs.map((job) => (
          <JobCard
            key={job.id}
            job={job}
            expanded={expandedJob === job.id}
            onToggle={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
          />
        ))
      )}
    </div>
  </div>
);

const TicketsScreen = ({
  products,
  packTab,
  setPackTab,
  qty,
  setQty,
  selectedProduct,
  preview,
  wallet,
  busyId,
  onWallet,
  onConvert,
}) => {
  const credits = Number(wallet?.wallet_available ?? wallet?.available_credits ?? 0);
  const packPrice = Number(selectedProduct?.price_inr || 0);
  const canConvert = packPrice > 0 && credits >= packPrice;
  return (
    <div className="pb-6">
    <div className="bg-gradient-to-r from-[#1B4F9C] to-[#153E7A] px-4 pb-5 pt-3 text-white">
      <p className="text-[22px] font-extrabold">Buy tickets</p>
      <p className="mt-0.5 text-[12px] font-semibold text-white/80">1 ticket = 1 successful RSA · +18% GST at checkout</p>
    </div>
    <div className="px-4 pt-3">
      <div className="flex rounded-2xl bg-slate-100 p-1">
        {[
          ['fuel', 'Fuel'],
          ['roadside', 'Roadside'],
          ['towing', 'Towing'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => {
              setPackTab(id);
              setQty(10);
            }}
            className={`flex-1 rounded-xl py-2 text-[12px] font-extrabold ${
              packTab === id ? 'bg-white text-[#1B4F9C] shadow-sm' : 'text-slate-500'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
    {!selectedProduct || !preview ? (
      <p className="px-5 pt-8 text-center text-[13px] font-semibold text-slate-500">No ticket packs</p>
    ) : (
      <div className="px-4 pt-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-[16px] font-extrabold text-slate-900">{selectedProduct.name}</p>
              <p className="text-[11px] font-semibold text-slate-500">
                {formatInr(selectedProduct.price_inr)} / ticket + 18% GST
              </p>
            </div>
            {preview.pct > 0 ? (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-extrabold text-emerald-700">
                Save {preview.pct}%
              </span>
            ) : null}
          </div>
          <p className="mt-3 text-[11px] font-bold uppercase tracking-wide text-slate-400">Bulk buy</p>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {BULK.map((opt) => {
              const p = bulkPreview(selectedProduct, opt.qty);
              const on = qty === opt.qty;
              return (
                <button
                  key={opt.qty}
                  type="button"
                  onClick={() => setQty(opt.qty)}
                  className={`rounded-2xl border px-2 py-2.5 text-center ${
                    on ? 'border-[#1B4F9C] bg-[#EFF6FF]' : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  {opt.qty === 10 ? (
                    <p className={`text-[9px] font-extrabold ${on ? 'text-[#1B4F9C]' : 'text-slate-400'}`}>Best</p>
                  ) : (
                    <p className="text-[9px] opacity-0">.</p>
                  )}
                  <p className={`text-[18px] font-extrabold ${on ? 'text-[#1B4F9C]' : 'text-slate-900'}`}>{opt.qty}</p>
                  <p className="text-[10px] font-bold text-slate-500">
                    {opt.discountPct ? `Save ${opt.discountPct}%` : 'Single'}
                  </p>
                  <p className={`text-[11px] font-extrabold ${on ? 'text-[#1B4F9C]' : 'text-slate-700'}`}>
                    {formatInr(p.final)}
                  </p>
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            <p className="text-[11px] font-bold text-slate-400">Pay total</p>
            <p className="text-[26px] font-extrabold text-[#0F2744]">{formatInr(preview.final)}</p>
            <p className="text-[11px] font-semibold text-slate-500">
              {qty} tickets · {preview.credits} credits · + 18% GST
            </p>
          </div>
          <div className="mt-3 space-y-1.5">
            {(selectedProduct.allowed_service_types || []).map((s) => (
              <p key={s} className="flex items-center gap-1.5 text-[12px] font-semibold text-slate-700">
                <Check size={14} className="text-[#1B4F9C]" />
                {s}
              </p>
            ))}
          </div>
        </div>
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3.5">
          <p className="text-[13px] font-extrabold text-slate-900">How tickets work</p>
          {[
            ['1 ticket = 1 RSA', 'Ticket deducts only after you complete the job successfully.'],
            ['Go Online for jobs', 'Nearby Fuel / Roadside / Towing alerts when you are online.'],
            ['Bulk packs save more', 'Buy 5 or 10 tickets — discount applies before GST at checkout.'],
          ].map(([t, b]) => (
            <div key={t} className="mt-2.5">
              <p className="text-[12px] font-extrabold text-slate-800">{t}</p>
              <p className="text-[11px] font-semibold leading-snug text-slate-500">{b}</p>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={onWallet}
          className="mt-3 flex w-full items-center justify-between rounded-2xl border border-[#1B4F9C]/20 bg-[#EFF6FF] px-3.5 py-3 text-left"
        >
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#1B4F9C]">Wallet</p>
            <p className="text-[15px] font-extrabold text-slate-900">{formatInr(credits)}</p>
            <p className="text-[11px] font-semibold text-slate-500">GST nahi · wallet se pack lo</p>
          </div>
          <span className="text-[11px] font-extrabold text-[#1B4F9C]">Open</span>
        </button>
        <button
          type="button"
          disabled={!canConvert || !!busyId}
          onClick={() => onConvert?.(selectedProduct)}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-2xl bg-[#0F2744] py-3 text-sm font-extrabold text-white disabled:opacity-40"
        >
          {busyId === selectedProduct.id ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowLeftRight size={16} />
          )}
          {canConvert ? `Get tickets from wallet · ${formatInr(packPrice)}` : 'Wallet se convert'}
        </button>
        <button
          type="button"
          className="mt-2 w-full rounded-2xl bg-[#1B4F9C] py-3 text-sm font-extrabold text-white"
        >
          Continue to pay
        </button>
        {products.length === 0 ? null : (
          <p className="mt-2 text-center text-[10px] font-semibold text-slate-400">
            Preview only · payment partner phone par hota hai
          </p>
        )}
      </div>
    )}
  </div>
  );
};

const ProfileScreen = ({
  profile,
  displayName,
  typeLabel,
  cityLabel,
  specs,
  tickets,
  liveSteps,
  isVerifiedLive,
  locale,
  setLocale,
  uploading,
  onUploadLogo,
  onEdit,
  onPublic,
  onHours,
  onHistory,
  onWallet,
  wallet,
  purchaseCount,
  onTeam,
  onNotif,
  onTogglePublic,
  hoursSummary,
  crewCount,
  notifSummary,
  onTab,
}) => {
  const done = liveSteps.filter((s) => s.done).length;
  return (
    <div className="pb-8">
      <div className="bg-gradient-to-br from-[#0F2744] via-[#1B4F9C] to-[#2563EB] px-4 pb-5 pt-3 text-white">
        <div className="flex items-center gap-3">
          <label className="relative h-16 w-16 shrink-0 cursor-pointer">
            {profile.logo_url ? (
              <img src={profile.logo_url} alt="" className="h-16 w-16 rounded-full border-2 border-white/40 object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15">
                {uploading ? <Loader2 size={18} className="animate-spin" /> : <User size={28} />}
              </div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#0F2744] shadow">
              <Camera size={12} />
            </span>
            {isVerifiedLive ? (
              <span className="absolute -right-1 -top-1 text-emerald-400">
                <ShieldCheck size={18} fill="#10b981" color="#fff" />
              </span>
            ) : null}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onUploadLogo(file);
                e.target.value = '';
              }}
            />
          </label>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="truncate text-[16px] font-extrabold">{displayName}</p>
              {isVerifiedLive ? (
                <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-extrabold uppercase text-emerald-800">
                  <ShieldCheck size={10} />
                  Verified
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 text-[11px] text-white/75">{profile.email || profile.phone || '—'}</p>
            <p className="text-[11px] text-white/60">
              {typeLabel} · Tap photo to upload
            </p>
          </div>
        </div>
      </div>

      {!isVerifiedLive ? (
        <CardSection title="Steps to go live">
          <p className="px-3 pt-2 text-[11px] font-bold text-slate-500">
            {done}/{liveSteps.length} complete
          </p>
          <div className="mx-3 mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-[#1B4F9C]" style={{ width: `${(done / liveSteps.length) * 100}%` }} />
          </div>
          {liveSteps.map((step, i) => (
            <button
              key={step.id}
              type="button"
              onClick={() => {
                if (step.id === 'photo') return;
                if (step.id === 'city') onEdit('city');
                else if (step.id === 'specialization') onEdit('specialization');
                else if (step.id === 'service') onEdit('radius');
                else if (step.id === 'docs' || step.id === 'admin') onPublic();
                else onPublic();
              }}
              className="flex w-full items-center gap-2.5 px-3 py-2.5"
            >
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-extrabold ${
                  step.done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                }`}
              >
                {step.done ? <Check size={12} /> : i + 1}
              </span>
              <span className={`flex-1 text-left text-[12px] font-semibold ${step.done ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                {step.title}
              </span>
              {step.done ? <Check size={16} className="text-emerald-500" /> : <ChevronRight size={16} className="text-slate-300" />}
            </button>
          ))}
          <p className="px-3 pb-3 text-[11px] font-semibold leading-snug text-slate-400">
            After all steps + approval, a Verified badge appears on your photo
          </p>
        </CardSection>
      ) : null}

      <CardSection title="Ticket balance">
        <div className="m-2 overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F2744] to-[#1B4F9C] p-3.5 text-white">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-2xl font-extrabold">{tickets?.total_available ?? 0}</p>
              <p className="text-[11px] text-white/70">Available</p>
            </div>
            <div>
              <p className="text-2xl font-extrabold">{Math.round(Number(tickets?.available_credits || 0))}</p>
              <p className="text-[11px] text-white/70">Credits</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onTab('tickets')}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-white py-2 text-[12px] font-extrabold text-[#0F2744]"
          >
            <Ticket size={14} />
            Buy tickets
          </button>
        </div>
        {(tickets?.products || [])
          .filter((p) => (p.available || 0) > 0 || (p.reserved || 0) > 0)
          .map((p) => (
            <Row key={p.product_id} title={p.name} value={String(p.available)} last />
          ))}
      </CardSection>

      <CardSection title="Ticket purchase history">
        <Row
          icon={<FileText size={16} />}
          title="Ticket purchase history"
          value={purchaseCount ? `${purchaseCount} invoices` : 'No purchases'}
          onPress={onHistory}
        />
        <button
          type="button"
          onClick={onWallet}
          className="flex w-full items-center justify-between border-b border-slate-100 bg-[#EFF6FF] px-3 py-2.5 text-left"
        >
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#1B4F9C]">Wallet balance</p>
            <p className="text-[18px] font-extrabold text-slate-900">
              {formatInr(Number(wallet?.wallet_available ?? wallet?.available_credits ?? 0))}
            </p>
          </div>
          {Number(wallet?.repay_due || 0) > 0 ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-800">
              Due {formatInr(wallet.repay_due)}
            </span>
          ) : (
            <ChevronRight size={14} className="text-slate-300" />
          )}
        </button>
        <Row
          icon={<Wallet size={16} />}
          title="Wallet"
          value="Balance, convert & tickets"
          onPress={onWallet}
        />
        <Row
          icon={<ArrowLeftRight size={16} />}
          title="Wallet se tickets"
          value="Convert credits"
          onPress={onWallet}
          last
        />
      </CardSection>

      <CardSection title="Personal info">
        <Row icon={<User size={16} />} title="Full name" value={displayName || 'Not set'} onPress={() => onEdit('name')} />
        <Row icon={<Mail size={16} />} title="Email" value={profile.email || 'Not set'} onPress={() => onEdit('email')} />
        <Row icon={<Phone size={16} />} title="Phone" value={profile.phone || 'Not set'} onPress={() => onEdit('phone')} last />
      </CardSection>

      <CardSection title="About">
        <Row
          icon={<Sparkles size={16} />}
          title="Bio"
          value={profile.bio ? profile.bio : 'AI write / edit'}
          onPress={() => onEdit('bio')}
          last
        />
      </CardSection>

      <CardSection title="Location & service">
        <Row
          icon={<Navigation size={16} />}
          title="Edit city / area"
          value={profile.city && profile.area ? `${profile.city}, ${profile.area}` : 'Detected from GPS'}
          onPress={() => onEdit('city')}
        />
        <Row
          icon={<MapPin size={16} />}
          title="Service area"
          value={profile.service_radius_km ? `${profile.service_radius_km} km` : 'Set on map'}
          onPress={() => onEdit('radius')}
          last
        />
      </CardSection>

      <CardSection title="Public profile">
        <Row
          icon={<Globe size={16} />}
          title="Public profile"
          value={isVerifiedLive ? 'Live ✓' : profile.kyc_status === 'pending' ? 'KYC pending' : 'Photo + KYC docs'}
          onPress={onPublic}
          last
        />
      </CardSection>

      <CardSection title="Professional">
        <Row
          icon={<Briefcase size={16} />}
          title="Partner type"
          value={typeLabel}
          onPress={() => onEdit('type')}
        />
        <Row
          icon={<Briefcase size={16} />}
          title="Specialization"
          value={specs.length ? specs.join(' · ') : 'Not set'}
          onPress={() => onEdit('specialization')}
        />
        <Row
          icon={<Clock size={16} />}
          title="Experience"
          value={profile.experience_years ? `${profile.experience_years} years` : 'Not set'}
          onPress={() => onEdit('experience')}
          last
        />
      </CardSection>

      <CardSection title="Language">
        {LANGS.map((l, i) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setLocale(l.code)}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left"
          >
            <span className={`h-3.5 w-3.5 rounded-full border-2 ${locale === l.code ? 'border-[#1B4F9C] bg-[#1B4F9C]' : 'border-slate-300'}`} />
            <span className="text-[13px] font-semibold text-slate-800">{l.label}</span>
          </button>
        ))}
      </CardSection>

      <CardSection title="App settings">
        <Row
          icon={<User size={16} />}
          title="Team"
          value={crewCount ? `${crewCount} workers` : 'Add worker'}
          onPress={onTeam}
        />
        <Row
          icon={<Clock size={16} />}
          title="Working hours"
          value={hoursSummary || 'Sun–Sat'}
          onPress={onHours}
        />
        <Row
          icon={<Globe size={16} />}
          title="Public page"
          value={profile.is_public ? 'ON' : 'OFF'}
          onPress={onTogglePublic}
          last
        />
      </CardSection>

      <CardSection title="Notifications">
        <Row icon={<Bell size={16} />} title="Notifications" value={notifSummary} onPress={onNotif} last />
      </CardSection>

      <CardSection title="Account">
        <Row title="Change password" />
        <Row title="Help & support" />
        <Row title="Terms of service" />
        <Row title="Privacy policy" last />
      </CardSection>
      <p className="px-4 pt-1 text-[11px] text-slate-400">{cityLabel}</p>
    </div>
  );
};

const PublicScreen = ({ profile, isVerifiedLive, onBack, onEdit, onUploadLogo, uploading }) => {
  const kyc = profile.kyc_status || 'none';
  const color = kyc === 'verified' ? '#059669' : kyc === 'rejected' ? '#DC2626' : '#D97706';
  const docs = [
    { type: 'selfie', label: 'Selfie', required: true },
    { type: 'aadhaar_front', label: 'Aadhaar front', required: true },
    { type: 'aadhaar_back', label: 'Aadhaar back', required: true },
    { type: 'pan', label: 'PAN', required: false },
    { type: 'shop_license', label: 'Shop license', required: false },
  ];
  const findDoc = (type) =>
    (profile.documents || []).find((d) =>
      type === 'aadhaar_front' ? d.doc_type === 'aadhaar_front' || d.doc_type === 'aadhaar' : d.doc_type === type,
    );

  return (
    <div className="pb-8">
      <div className="flex items-center gap-2 bg-white px-3 py-3">
        <button type="button" onClick={onBack} className="rounded-full p-1 hover:bg-slate-100">
          <ChevronRight className="rotate-180 text-slate-700" size={20} />
        </button>
        <div>
          <p className="text-[16px] font-extrabold text-slate-900">Public profile</p>
          <p className="text-[11px] font-semibold text-slate-500">Customers see this page</p>
        </div>
      </div>
      <div className="px-4 pt-3">
        <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-extrabold" style={{ background: `${color}18`, color }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
          {isVerifiedLive ? 'LIVE' : `Not public · ${String(kyc).toUpperCase()}`}
        </div>
        <p className="mt-4 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Profile photo</p>
        <label className="mt-2 flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white py-6">
          {profile.logo_url ? (
            <img src={profile.logo_url} alt="" className="h-20 w-20 rounded-full object-cover" />
          ) : (
            <Camera size={32} className="text-[#1B4F9C]" />
          )}
          <p className="mt-2 text-[12px] font-semibold text-slate-500">
            {uploading ? 'Uploading…' : 'Tap to change photo'}
          </p>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUploadLogo(file);
              e.target.value = '';
            }}
          />
        </label>
        <p className="mt-4 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Details</p>
        <button type="button" onClick={() => onEdit('name')} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-[13px] font-semibold text-slate-900">
          {profile.shop_name || 'Shop name'}
        </button>
        <button type="button" onClick={() => onEdit('city')} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-[13px] font-semibold text-slate-900">
          {profile.city || 'City'}
        </button>
        <button type="button" onClick={() => onEdit('area')} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-[13px] font-semibold text-slate-900">
          {profile.area || 'Area'}
        </button>
        <button type="button" onClick={() => onEdit('bio')} className="mt-2 min-h-[72px] w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-[13px] font-semibold text-slate-900">
          {profile.bio || 'About your shop'}
        </button>
        <button
          type="button"
          onClick={() => onEdit('bio')}
          className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-[#1B4F9C] px-3 py-2 text-[12px] font-extrabold text-white"
        >
          <Sparkles size={13} />
          AI write bio
        </button>
        <p className="mt-4 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">KYC documents</p>
        <p className="mt-1 text-[11px] font-semibold text-slate-500">Selfie + Aadhaar front + back required</p>
        <div className="mt-2 space-y-2">
          {docs.map((row) => {
            const d = findDoc(row.type);
            return (
              <div key={row.type} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white p-2.5">
                {d?.file_url ? (
                  <img src={d.file_url} alt="" className="h-11 w-11 rounded-lg object-cover" />
                ) : (
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                    <Camera size={16} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold text-slate-900">{row.label}</p>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {row.required ? 'Required' : 'Optional'} · {d ? d.status : 'Not uploaded'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const HoursScreen = ({ slots, saving, onBack, onSave }) => {
  const [draft, setDraft] = useState(() => normalizeSlots(slots));
  const [preset, setPreset] = useState(null);

  const applyPreset = (p) => {
    setPreset(p.id);
    setDraft((prev) =>
      normalizeSlots(prev).map((s) =>
        p.id === '24h'
          ? { ...s, is_enabled: true, start_time: p.start, end_time: p.end }
          : s.is_enabled
            ? { ...s, start_time: p.start, end_time: p.end }
            : s,
      ),
    );
  };

  const updateDay = (day, patch) => {
    setPreset(null);
    setDraft((prev) => prev.map((s) => (s.day_of_week === day ? { ...s, ...patch } : s)));
  };

  return (
    <div className="pb-8">
      <div className="flex items-center gap-2 bg-white px-3 py-3">
        <button type="button" onClick={onBack} className="rounded-full p-1 hover:bg-slate-100">
          <ChevronRight className="rotate-180 text-slate-700" size={20} />
        </button>
        <div>
          <p className="text-[16px] font-extrabold text-slate-900">Working hours</p>
          <p className="text-[11px] font-semibold text-slate-500">Days + time · {summarizeHours(draft)}</p>
        </div>
      </div>
      <div className="px-4 pt-3">
        <div className="flex flex-wrap gap-1.5">
          {HOUR_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                preset === p.id ? 'bg-[#1B4F9C] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-100 bg-white">
          {draft.map((s) => (
            <div
              key={s.day_of_week}
              className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5 last:border-b-0"
            >
              <button
                type="button"
                onClick={() => updateDay(s.day_of_week, { is_enabled: !s.is_enabled })}
                className={`relative h-6 w-11 shrink-0 rounded-full ${s.is_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
              >
                <span
                  className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow"
                  style={{ left: s.is_enabled ? 22 : 2 }}
                />
              </button>
              <span className="w-10 text-[12px] font-extrabold text-slate-800">{DAY_LABELS[s.day_of_week]}</span>
              {s.is_enabled ? (
                <div className="flex flex-1 items-center gap-1.5">
                  <input
                    type="time"
                    value={s.start_time}
                    onChange={(e) => updateDay(s.day_of_week, { start_time: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-1 text-[12px] font-bold text-slate-800"
                  />
                  <span className="text-[11px] font-bold text-slate-400">–</span>
                  <input
                    type="time"
                    value={s.end_time}
                    onChange={(e) => updateDay(s.day_of_week, { end_time: e.target.value })}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-1 text-[12px] font-bold text-slate-800"
                  />
                </div>
              ) : (
                <span className="text-[11px] font-semibold text-slate-400">Off</span>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(draft)}
          className="mt-4 w-full rounded-2xl bg-[#1B4F9C] py-3 text-sm font-extrabold text-white disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save hours'}
        </button>
      </div>
    </div>
  );
};

const JobCard = ({ job, expanded, onToggle }) => {
  const tint = serviceTint(job.service_type);
  const covered = isMemberJob(job);
  const isTowing = String(job.service_type || '').toLowerCase().includes('tow');
  return (
    <button type="button" onClick={onToggle} className="mb-3 w-full rounded-[18px] border border-slate-200 bg-white p-3.5 text-left shadow-sm">
      <div className="flex items-start gap-2.5">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: tint.bg, color: tint.fg }}>
          <Car size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-extrabold text-slate-900">{job.service_type || tint.label}</p>
          <p className="text-[11px] font-semibold text-slate-400">{timeAgo(job.requested_at || job.assigned_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`rounded-lg px-2 py-0.5 text-[10px] font-extrabold ${covered ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
            {covered ? 'MEMBER' : 'QUOTE'}
          </span>
          <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
            {job.request_status || '—'}
          </span>
        </div>
      </div>
      <div className="mt-2.5 space-y-1 text-[12px] font-semibold text-slate-600">
        <p className="flex items-center gap-1.5">
          <User size={13} className="text-slate-400" />
          {job.customer_name || 'Customer'}
        </p>
        {job.vehicle_number || job.car_number ? (
          <p className="flex items-center gap-1.5">
            <Car size={13} className="text-slate-400" />
            {job.vehicle_number || job.car_number}
          </p>
        ) : null}
        <p className="flex items-start gap-1.5 text-[#E02020]">
          <Zap size={13} className="mt-0.5 shrink-0" />
          {job.service_type || job.problem || 'Breakdown help needed'}
        </p>
      </div>
      <div className="mt-2.5 rounded-xl bg-slate-50 p-2.5">
        <p className="text-[10px] font-extrabold uppercase text-[#1B4F9C]">
          {isTowing ? 'Pickup / breakdown' : 'Vehicle location'}
        </p>
        <p className="mt-0.5 text-[12px] font-semibold leading-snug text-slate-700">
          {job.pickup_address || job.location || '—'}
        </p>
        {isTowing ? (
          <>
            <p className="mt-2 text-[10px] font-extrabold uppercase text-[#E02020]">Drop</p>
            <p className="mt-0.5 text-[12px] font-semibold leading-snug text-slate-700">
              {job.drop_location || 'Drop to be confirmed'}
            </p>
          </>
        ) : null}
      </div>
      {expanded ? (
        <div className="mt-2.5 space-y-2">
          {job.customer_quoted_amount || job.agreed_amount ? (
            <p className="text-[12px] font-bold text-slate-700">
              Rate: ₹{job.agreed_amount || job.customer_quoted_amount}
            </p>
          ) : (
            <p className="text-[12px] font-semibold text-slate-500">Rate pending</p>
          )}
          <div className="flex gap-2">
            <span className="inline-flex flex-1 items-center justify-center gap-1 rounded-xl bg-[#1B4F9C] py-2 text-[12px] font-extrabold text-white">
              <Navigation size={14} /> Navigate
            </span>
            <span className="inline-flex items-center justify-center gap-1 rounded-xl border border-[#1B4F9C] px-3 py-2 text-[12px] font-extrabold text-[#1B4F9C]">
              <Phone size={14} /> Call
            </span>
            <span className="inline-flex items-center justify-center gap-1 rounded-xl border border-slate-200 px-3 py-2 text-[12px] font-extrabold text-slate-600">
              <MessageCircle size={14} /> Chat
            </span>
          </div>
        </div>
      ) : null}
      <p className="mt-2 flex items-center justify-center gap-1 text-[11px] font-bold text-[#1B4F9C]">
        {expanded ? 'Hide' : 'Details'}
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </p>
    </button>
  );
};

const Kpi = ({ icon, iconBg, bg, n, l }) => (
  <div className={`rounded-2xl border border-slate-200 bg-gradient-to-b ${bg} to-white px-2.5 py-2.5`}>
    <span className="mb-2 flex h-7 w-7 items-center justify-center rounded-[9px]" style={{ background: iconBg }}>
      {icon}
    </span>
    <p className="text-[22px] font-extrabold leading-none text-[#0F2744]">{n}</p>
    <p className="mt-1 text-[11px] font-bold text-slate-500">{l}</p>
  </div>
);

const SectionHead = ({ accent = '#1B4F9C', title, sub, action, onAction }) => (
  <div className="mb-3 flex items-center gap-2.5 px-4">
    <span className="h-8 w-1 rounded" style={{ background: accent }} />
    <div className="min-w-0 flex-1">
      <p className="text-[18px] font-extrabold text-slate-900">{title}</p>
      <p className="text-[12px] font-semibold text-slate-500">{sub}</p>
    </div>
    {action ? (
      <button type="button" onClick={onAction} className="flex items-center gap-0.5 text-[13px] font-extrabold text-[#2563EB]">
        {action}
        <ChevronRight size={14} />
      </button>
    ) : null}
  </div>
);

const CardSection = ({ title, children }) => (
  <div className="px-3 pt-3">
    <p className="mb-1.5 px-1 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">{title}</p>
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm">{children}</div>
  </div>
);

const Row = ({ icon, title, value, onPress, last }) => (
  <button
    type="button"
    onClick={onPress}
    disabled={!onPress}
    className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left ${last ? '' : 'border-b border-slate-100'}`}
  >
    {icon ? (
      <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#1B4F9C]">{icon}</span>
    ) : null}
    <span className="min-w-0 flex-1 text-[13px] font-semibold text-slate-800">{title}</span>
    {value ? <span className="max-w-[42%] truncate text-[11px] font-bold text-slate-400">{value}</span> : null}
    {onPress ? <ChevronRight size={14} className="text-slate-300" /> : null}
  </button>
);

const EditSheet = ({
  field,
  value,
  setValue,
  extra,
  setExtra,
  specs,
  setSpecs,
  mapLat,
  mapLng,
  mapHex,
  mapShape,
  onMapChange,
  saving,
  aiWriting,
  onAiWrite,
  onClose,
  onSave,
}) => {
  const title = {
    specialization: 'Specialization',
    experience: 'Experience',
    name: 'Full name',
    phone: 'Phone',
    email: 'Email',
    city: 'City',
    area: 'Area',
    place: 'City / area',
    radius: 'Service area',
    type: 'Partner type',
    bio: 'About / bio',
  }[field] || 'Edit';

  return (
    <div className="absolute inset-0 z-30 flex items-end bg-black/40" onClick={onClose}>
      <div className="max-h-[85%] w-full overflow-y-auto rounded-t-3xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
        <p className="text-[15px] font-extrabold text-slate-900">{title}</p>
        {field === 'specialization' ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {SPECS.map((s) => {
              const on = specs.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpecs((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${on ? 'bg-[#1B4F9C] text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        ) : field === 'radius' ? (
          <div className="mt-3">
            <p className="text-[12px] font-semibold text-slate-500">
              Circle ya hexagon — ek hi. Pin mechanic location. Jobs is area ke andar dikhenge (1–50 km)
            </p>
            <ServiceAreaMapEditor
              lat={mapLat}
              lng={mapLng}
              radiusKm={value}
              hex={mapHex}
              shape={mapShape || 'circle'}
              onChange={onMapChange}
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {[5, 10, 15, 20, 25, 30, 40, 50].map((km) => (
                <button
                  key={km}
                  type="button"
                  onClick={() => {
                    setValue(String(km));
                    onMapChange?.({
                      lat: mapLat,
                      lng: mapLng,
                      hex: regularHexagon({ lat: mapLat, lng: mapLng }, km),
                      radiusKm: km,
                    });
                  }}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${
                    String(value) === String(km) ? 'bg-[#1B4F9C] text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {km} km
                </button>
              ))}
            </div>
            <input
              type="number"
              min="1"
              max="50"
              className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1B4F9C]"
              value={value}
              onChange={(e) => {
                const km = e.target.value;
                setValue(km);
                const n = Math.max(1, Math.min(50, Math.round(Number(km) || 15)));
                onMapChange?.({
                  lat: mapLat,
                  lng: mapLng,
                  hex: regularHexagon({ lat: mapLat, lng: mapLng }, n),
                  radiusKm: n,
                });
              }}
            />
          </div>
        ) : field === 'place' ? (
          <div className="mt-3 space-y-2">
            <input
              autoFocus
              placeholder="City"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1B4F9C]"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <input
              placeholder="Area"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1B4F9C]"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
            />
          </div>
        ) : field === 'type' ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {[
              ['mechanic', 'Mechanic'],
              ['towing', 'Towing'],
              ['both', 'Mech + Towing'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setValue(id)}
                className={`rounded-full px-3 py-1.5 text-[12px] font-bold ${
                  value === id ? 'bg-[#1B4F9C] text-white' : 'bg-slate-100 text-slate-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        ) : field === 'bio' ? (
          <div className="mt-3">
            <button
              type="button"
              disabled={aiWriting}
              onClick={onAiWrite}
              className="mb-2 inline-flex items-center gap-1.5 rounded-xl bg-[#1B4F9C] px-3 py-1.5 text-[12px] font-extrabold text-white disabled:opacity-60"
            >
              {aiWriting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {aiWriting ? 'Writing…' : 'AI write'}
            </button>
            <textarea
              autoFocus
              rows={5}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1B4F9C]"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="About this shop…"
            />
          </div>
        ) : (
          <input
            autoFocus
            className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px] outline-none focus:border-[#1B4F9C]"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 rounded-xl bg-slate-100 py-2.5 text-[13px] font-bold text-slate-600">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || aiWriting}
            onClick={onSave}
            className="flex-1 rounded-xl bg-[#1B4F9C] py-2.5 text-[13px] font-bold text-white disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const walletReason = (reason) => {
  const r = String(reason || '');
  if (/redeem|ticket/i.test(r)) return 'Tickets from wallet';
  if (/repay/i.test(r)) return 'Repayment';
  if (/grant|admin|advance/i.test(r)) return 'Credit added';
  if (/refer/i.test(r)) return 'Referral reward';
  return r || 'Wallet';
};

const WalletScreen = ({ wallet, products, busyId, onBack, onConvert }) => {
  const credits = Number(wallet?.wallet_available ?? wallet?.available_credits ?? 0);
  const packs = (products || []).filter((p) => Number(p.price_inr || 0) > 0);
  const ledger = Array.isArray(wallet?.ledger) ? wallet.ledger : [];
  return (
    <div className="pb-8">
      <div className="flex items-center gap-2 bg-white px-3 py-3">
        <button type="button" onClick={onBack} className="rounded-full p-1 hover:bg-slate-100">
          <ChevronRight className="rotate-180 text-slate-700" size={20} />
        </button>
        <div>
          <p className="text-[16px] font-extrabold text-slate-900">Wallet</p>
          <p className="text-[11px] font-semibold text-slate-500">Balance, repayments & tickets</p>
        </div>
      </div>
      <div className="mx-4 mt-3 overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F2744] to-[#1B4F9C] p-4 text-white">
        <p className="text-[11px] font-extrabold uppercase tracking-wide text-white/70">Wallet balance</p>
        <p className="mt-1 text-[32px] font-black leading-none">{formatInr(credits)}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-bold">
            {wallet?.tickets_available ?? 0} tickets
          </span>
          {Number(wallet?.repay_due || 0) > 0 ? (
            <span className="rounded-full bg-red-500/40 px-2.5 py-1 text-[11px] font-bold">
              Due {formatInr(wallet.repay_due)}
            </span>
          ) : null}
        </div>
      </div>
      {wallet?.error ? (
        <p className="mx-4 mt-3 rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-800">
          {wallet.error}
        </p>
      ) : null}
      <div className="mx-4 mt-3 rounded-2xl border border-slate-100 bg-white p-3.5">
        <div className="flex items-center gap-2">
          <ArrowLeftRight size={16} className="text-[#1B4F9C]" />
          <p className="text-[15px] font-extrabold text-slate-900">Use wallet for tickets</p>
        </div>
        <p className="mt-1 text-[12px] font-medium leading-snug text-slate-500">
          Buy a ticket pack from wallet — no GST. Tickets and ticket credits both get added.
        </p>
        {packs.length === 0 ? (
          <p className="mt-3 text-[12px] font-semibold text-slate-500">No ticket packs</p>
        ) : credits <= 0 ? (
          <p className="mt-3 text-[12px] font-semibold text-slate-500">Wallet empty — pehle credits add karo</p>
        ) : (
          packs.map((p) => {
            const price = Number(p.price_inr || 0);
            const enough = price > 0 && price <= credits;
            return (
              <div key={p.id} className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-extrabold text-slate-900">{p.name}</p>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {p.tickets_count || 1} tickets · {formatInr(price)}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!enough || !!busyId}
                  onClick={() => onConvert(p)}
                  className="rounded-xl bg-[#1B4F9C] px-3 py-2 text-[11px] font-extrabold text-white disabled:opacity-40"
                >
                  {busyId === p.id ? '…' : 'Get tickets'}
                </button>
              </div>
            );
          })
        )}
      </div>
      <div className="mx-4 mt-3 rounded-2xl border border-slate-100 bg-white p-3.5">
        <p className="text-[15px] font-extrabold text-slate-900">History</p>
        {ledger.length === 0 ? (
          <p className="mt-2 text-[12px] font-semibold text-slate-500">No wallet history yet</p>
        ) : (
          ledger.map((row) => {
            const credit = Number(row.delta) > 0;
            return (
              <div key={row.id} className="mt-2 flex items-center justify-between border-t border-slate-50 pt-2">
                <div>
                  <p className="text-[13px] font-bold text-slate-800">{walletReason(row.reason)}</p>
                  <p className="text-[11px] text-slate-400">
                    {row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : ''}
                  </p>
                </div>
                <p className={`text-[13px] font-extrabold ${credit ? 'text-emerald-600' : 'text-red-600'}`}>
                  {credit ? '+' : '−'}
                  {formatInr(Math.abs(Number(row.delta)))}
                </p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const HistoryScreen = ({ purchases, openPurchase, setOpenPurchase, onBack, onBuy }) => {
  if (openPurchase) {
    const p = openPurchase;
    const total = Number(p.total_amount ?? p.amount_inr ?? 0);
    const gst = Number(p.gst_amount || 0);
    const base = Number(p.base_amount || (gst ? total - gst : total));
    return (
      <div className="pb-8">
        <div className="flex items-center gap-2 bg-white px-3 py-3">
          <button type="button" onClick={() => setOpenPurchase(null)} className="rounded-full p-1 hover:bg-slate-100">
            <ChevronRight className="rotate-180 text-slate-700" size={20} />
          </button>
          <div>
            <p className="text-[16px] font-extrabold text-slate-900">Invoice</p>
            <p className="text-[11px] font-semibold text-slate-500">{p.invoice_number || 'Ticket pack'}</p>
          </div>
        </div>
        <div className="mx-4 mt-3 rounded-2xl border border-slate-100 bg-white p-4">
          <p className="text-[15px] font-extrabold text-slate-900">{p.product_name || 'Ticket pack'}</p>
          <p className="mt-1 text-[12px] font-semibold text-slate-500">
            {formatDate(p.paid_at || p.created_at)} · {String(p.status || '').toUpperCase()}
          </p>
          <div className="mt-3 space-y-1.5 text-[13px] font-semibold text-slate-700">
            <p className="flex justify-between"><span>Tickets</span><span>{p.tickets_credited || 0}</span></p>
            {p.credits_credited ? (
              <p className="flex justify-between"><span>Credits</span><span>{Math.round(Number(p.credits_credited))}</span></p>
            ) : null}
            <p className="flex justify-between"><span>Taxable</span><span>{formatInr(base)}</span></p>
            {gst > 0 ? <p className="flex justify-between"><span>GST</span><span>{formatInr(gst)}</span></p> : null}
            {p.coupon_code ? (
              <p className="flex justify-between text-emerald-700">
                <span>Coupon {p.coupon_code}</span>
                <span>−{formatInr(p.coupon_discount || 0)}</span>
              </p>
            ) : null}
            <p className="flex justify-between border-t border-slate-100 pt-2 text-[16px] font-extrabold text-[#0F2744]">
              <span>Total</span>
              <span>{formatInr(total)}</span>
            </p>
          </div>
          {p.billing_name || p.gstin ? (
            <div className="mt-3 border-t border-slate-100 pt-3 text-[12px] font-semibold text-slate-500">
              {p.billing_name ? <p>{p.billing_name}</p> : null}
              {p.gstin ? <p>GSTIN {p.gstin}</p> : null}
              {p.billing_address ? <p>{p.billing_address}</p> : null}
            </div>
          ) : null}
          {p.razorpay_payment_id ? (
            <p className="mt-2 text-[10px] font-semibold text-slate-400">Pay ID {p.razorpay_payment_id}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-8">
      <div className="flex items-center gap-2 bg-white px-3 py-3">
        <button type="button" onClick={onBack} className="rounded-full p-1 hover:bg-slate-100">
          <ChevronRight className="rotate-180 text-slate-700" size={20} />
        </button>
        <div>
          <p className="text-[16px] font-extrabold text-slate-900">Ticket purchase history</p>
          <p className="text-[11px] font-semibold text-slate-500">{purchases.length} invoices</p>
        </div>
      </div>
      <div className="px-4 pt-3">
        {purchases.length === 0 ? (
          <div className="py-16 text-center">
            <FileText size={40} className="mx-auto text-slate-300" />
            <p className="mt-2 text-[14px] font-bold text-slate-500">No ticket purchases</p>
            <button
              type="button"
              onClick={onBuy}
              className="mt-4 rounded-xl bg-[#1B4F9C] px-4 py-2 text-[13px] font-extrabold text-white"
            >
              Buy tickets
            </button>
          </div>
        ) : (
          purchases.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setOpenPurchase(p)}
              className="mb-2.5 flex w-full items-center gap-2.5 rounded-[14px] border border-slate-200 bg-white p-3 text-left"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-[#EFF6FF] text-[#1B4F9C]">
                <FileText size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-extrabold text-slate-900">{p.product_name || 'Ticket pack'}</p>
                <p className="text-[11px] font-semibold text-slate-500">
                  {formatDate(p.paid_at || p.created_at)}
                  {p.invoice_number ? ` · ${p.invoice_number}` : ''}
                </p>
                <p className="text-[11px] font-semibold text-slate-500">{p.tickets_credited || 0} tickets</p>
              </div>
              <div className="text-right">
                <p className="text-[14px] font-extrabold text-slate-900">
                  {formatInr(p.total_amount ?? p.amount_inr)}
                </p>
                <p className="text-[11px] font-bold text-[#1B4F9C]">View invoice</p>
                <p className="text-[10px] font-bold uppercase text-slate-400">{p.status || 'paid'}</p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

const TeamScreen = ({ crew, saving, onBack, onAdd }) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  return (
    <div className="pb-8">
      <div className="flex items-center gap-2 bg-white px-3 py-3">
        <button type="button" onClick={onBack} className="rounded-full p-1 hover:bg-slate-100">
          <ChevronRight className="rotate-180 text-slate-700" size={20} />
        </button>
        <div>
          <p className="text-[16px] font-extrabold text-slate-900">Team</p>
          <p className="text-[11px] font-semibold text-slate-500">Add worker under this partner</p>
        </div>
      </div>
      <div className="px-4 pt-3">
        <div className="space-y-2 rounded-2xl border border-slate-100 bg-white p-3">
          {(crew || []).length === 0 ? (
            <p className="text-[12px] font-semibold text-slate-500">No workers yet</p>
          ) : (
            crew.map((c) => (
              <div key={c.id} className="flex items-center justify-between border-b border-slate-50 py-2 last:border-0">
                <div>
                  <p className="text-[13px] font-bold text-slate-900">{c.display_name || 'Worker'}</p>
                  <p className="text-[11px] font-semibold text-slate-500">{c.phone || '—'}</p>
                </div>
                <span className="text-[10px] font-bold uppercase text-slate-400">{c.status || 'active'}</span>
              </div>
            ))
          )}
        </div>
        <p className="mt-4 text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Add worker</p>
        <input
          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px]"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[14px]"
          placeholder="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <button
          type="button"
          disabled={saving || !name.trim() || !phone.trim()}
          onClick={() => {
            onAdd({ name: name.trim(), phone: phone.trim() });
            setName('');
            setPhone('');
          }}
          className="mt-3 w-full rounded-2xl bg-[#1B4F9C] py-3 text-sm font-extrabold text-white disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add worker'}
        </button>
      </div>
    </div>
  );
};

const NotifScreen = ({ prefs, setPrefs, onBack }) => (
  <div className="pb-8">
    <div className="flex items-center gap-2 bg-white px-3 py-3">
      <button type="button" onClick={onBack} className="rounded-full p-1 hover:bg-slate-100">
        <ChevronRight className="rotate-180 text-slate-700" size={20} />
      </button>
      <div>
        <p className="text-[16px] font-extrabold text-slate-900">Notifications</p>
        <p className="text-[11px] font-semibold text-slate-500">How this partner gets job alerts</p>
      </div>
    </div>
    <div className="mx-4 mt-3 overflow-hidden rounded-2xl border border-slate-100 bg-white">
      {[
        ['push', 'Push notification'],
        ['whatsapp', 'WhatsApp notification'],
        ['call', 'Call notification'],
      ].map(([key, label], i, arr) => (
        <div
          key={key}
          className={`flex items-center justify-between px-3 py-3 ${i < arr.length - 1 ? 'border-b border-slate-100' : ''}`}
        >
          <span className="text-[13px] font-semibold text-slate-800">{label}</span>
          <button type="button" onClick={() => setPrefs((p) => ({ ...p, [key]: !p[key] }))}>
            <span className={`relative block h-6 w-11 rounded-full ${prefs[key] ? 'bg-emerald-500' : 'bg-slate-300'}`}>
              <span
                className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow"
                style={{ left: prefs[key] ? 22 : 2 }}
              />
            </span>
          </button>
        </div>
      ))}
    </div>
  </div>
);

export default MechanicAppPreviewModal;
